import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT_MS = 20000;

// Der Maps-Key wird serverseitig ermittelt und pro Request als
// data-maps-geocoder-key am <html>-Element übergeben (includes/maps-config.php).
function geocoderKey() {
  if (typeof document === "undefined" || !document.documentElement || !document.documentElement.dataset) {
    return "";
  }

  const key = document.documentElement.dataset.mapsGeocoderKey;
  return typeof key === "string" ? key.trim() : "";
}

function isFiniteNumber(v) {
  return typeof v === "number" && isFinite(v);
}

function toCoordinate(v) {
  if (v === null || v === undefined || v === "") {
    return NaN;
  }

  return Number(v);
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
    this.requestId = 0;
  }

  cancelAutocomplete() {
    this.requestId++;

    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g, name) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.zip) {
      s = g.zip;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    } else if (!g.city && name) {
      // Ohne Orts- und Straßenangabe bleibt der Feature-Name die Beschriftung.
      p.push(name);
    }

    if (s) {
      p.push(s);
    }

    if (g.state && !g.street) {
      p.push(g.state);
    }

    if (g.country && !g.street) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  _features(res) {
    if (!res || !res.data || !Array.isArray(res.data.features)) {
      return [];
    }

    return res.data.features.filter(f => f && f.properties && f.geometry && Array.isArray(f.geometry.coordinates));
  }

  _geoaddress(l) {
    const p = l.properties;

    const value = k => {
      const v = p[k];
      if (typeof v === "string") {
        const t = v.trim();
        return t !== "" ? t : null;
      }
      if (isFiniteNumber(v)) {
        return String(v);
      }
      return null;
    };

    if (value("countrycode") != "DE") {
      return;
    }

    const lng = l.geometry.coordinates[0];
    const lat = l.geometry.coordinates[1];
    if (!isFiniteNumber(lat) || !isFiniteNumber(lng)) {
      return;
    }

    const type = value("type");
    const name = value("name");

    let street = value("street");
    const number = value("housenumber");
    let city = value("city");
    let district = value("district");
    const zip = value("postcode");

    if (type == "street") {
      street = street || name;
    } else if (type == "city") {
      city = city || name;
    } else if (type == "district") {
      district = district || name;
    } else if (!type) {
      // Legacy-Fallback für Antworten ohne properties.type
      if (p.osm_key == "highway") {
        street = street || name;
      } else if (name && city && name != city && !district) {
        if (p.osm_key == "boundary" || (p.osm_key == "place" && p.osm_value == "suburb")) {
          district = name;
        }
      }
    }

    const geoaddress = {};

    if (street) {
      geoaddress["street"] = street;
      if (number) {
        geoaddress["number"] = number;
      }
    }

    if (zip) {
      geoaddress["zip"] = zip;
    }

    if (city) {
      geoaddress["city"] = city;
    }

    if (district && district != city) {
      geoaddress["district"] = district;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = value("state") || undefined;
    geoaddress["country"] = value("country") || undefined;

    geoaddress["text"] = this._geoaddressText(geoaddress, name);

    if (!geoaddress["text"]) {
      return;
    }

    return geoaddress;
  }

  // Liefert die Treffer der jüngsten Suche. Abgebrochene oder durch eine
  // neuere Suche überholte Anfragen enden mit null, nie als offenes Promise.
  autocomplete(q) {
    this.cancelAutocomplete();

    const requestId = this.requestId;
    const query = typeof q === "string" ? q.trim() : "";
    const key = geocoderKey();

    if (!query || !key) {
      return Promise.resolve([]);
    }

    const request = {
      source: this.axios.CancelToken.source()
    };
    this.axiosRequest = request;

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", 10);
    params.append("key", key);

    const done = () => {
      if (this.axiosRequest === request) {
        this.axiosRequest = null;
      }
    };

    return this.axios.get(SEARCH_URL, {
      cancelToken: request.source.token,
      timeout: REQUEST_TIMEOUT_MS,
      params
    }).then(res => {
      done();

      if (requestId !== this.requestId) {
        return null;
      }

      const results = [];
      this._features(res).forEach(l => {
        const geoaddress = this._geoaddress(l);
        if (geoaddress) {
          results.push(geoaddress);
        }
      });

      return results;
    }, thrown => {
      done();

      if (this.axios.isCancel(thrown) || requestId !== this.requestId) {
        return null;
      }

      console.log(thrown);
      throw thrown;
    });
  }

  reverse(lat, lng) {
    const key = geocoderKey();
    if (!key) {
      return Promise.reject(new Error("geocoder key missing"));
    }

    const la = toCoordinate(lat);
    const lo = toCoordinate(lng);
    if (!isFiniteNumber(la) || !isFiniteNumber(lo) || la < -90 || la > 90 || lo < -180 || lo > 180) {
      return Promise.reject(new Error("invalid coordinates"));
    }

    const params = new URLSearchParams();
    params.append("lat", la);
    params.append("lon", lo);
    params.append("lang", "de");
    params.append("key", key);

    return this.axios.get(REVERSE_URL, {
      timeout: REQUEST_TIMEOUT_MS,
      params
    }).then(res => {
      const features = this._features(res);
      if (features.length > 0) {
        return this._geoaddress(features[0]);
      }

      throw new Error("invalid data");
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
